import { useEffect, useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Mail, Menu, X } from "lucide-react";
import { useLang } from "../i18n";
import { scrollToId } from "../hooks/useLenis";

const LINKS = [
  { id: "#home", key: "home" },
  { id: "#minerals", key: "minerals" },
  { id: "#services", key: "services" },
  { id: "#prices", key: "prices" },
  { id: "#about", key: "about" },
  { id: "#contact", key: "contact" },
];

export function Navbar() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("#home");
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 25 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(`#${e.target.id}`)),
      { rootMargin: "-40% 0px -55% 0px" }
    );
    LINKS.forEach((l) => {
      const el = document.querySelector(l.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-[100] transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-[#07111F]/85 backdrop-blur-xl border-b border-[#C9A227]/15" : "bg-transparent border-b border-transparent"
      }`}
    >
      <motion.div className="absolute bottom-0 left-0 right-0 h-[2px] origin-left bg-gradient-to-r from-[#C9A227] via-[#F5F7FA] to-[#C9A227]" style={{ scaleX: progress }} />
      <div className="mx-auto max-w-7xl px-5 md:px-8 h-[72px] flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => go("#home")} className="flex items-center gap-2 group" aria-label="MX Minerals home">
          <img src="/assets/logo-mark.png" alt="MX" className="h-8 w-auto group-hover:drop-shadow-[0_0_10px_rgba(201,162,39,0.5)] transition-[filter] duration-300" />
          <span className="hidden sm:block font-body text-[10px] tracking-[0.3em] text-[#EDF1F6] mt-1">MINERALS</span>
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <button
              key={l.key}
              data-testid={`nav-link-${l.key}`}
              onClick={() => go(l.id)}
              className={`font-body text-sm tracking-wide transition-colors duration-300 ${
                active === l.id ? "text-[#C9A227] font-semibold" : "text-[#EDF1F6] hover:text-[#C9A227]"
              }`}
            >
              {t.nav[l.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-email-link"
            href="mailto:INFO@MINERALSMX.COM"
            className="hidden xl:inline-flex items-center gap-1.5 text-[11px] font-medium tracking-[0.18em] text-[#EDF1F6]/85 hover:text-[#C9A227] transition-colors duration-300"
          >
            <Mail size={13} /> INFO@MINERALSMX.COM
          </a>
          <div className="flex items-center rounded-full border border-[#EDF1F6]/20 overflow-hidden" role="group" aria-label="Language switcher">
            {["en", "es"].map((l) => (
              <button
                key={l}
                data-testid={`lang-switch-${l}`}
                onClick={() => setLang(l)}
                aria-pressed={lang === l}
                className={`px-3 py-1.5 text-xs font-semibold tracking-widest transition-colors duration-300 ${
                  lang === l ? "bg-[#C9A227] text-[#07111F]" : "text-[#EDF1F6] hover:text-[#F5F7FA]"
                }`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <button
            data-testid="nav-ports-button"
            onClick={() => window.dispatchEvent(new CustomEvent("mx:ports-click"))}
            className="hidden md:flex items-center rounded-full border border-[#C9A227]/60 px-5 py-2 text-xs font-bold tracking-widest text-[#C9A227] hover:bg-[#C9A227] hover:text-[#07111F] transition-[background-color,color] duration-300"
          >
            {t.ports.navLabel}
          </button>
          <button
            data-testid="nav-request-cta"
            onClick={() => go("#request")}
            className="hidden md:block rounded-full bg-[#C9A227] px-5 py-2 text-xs font-bold tracking-widest text-[#07111F] hover:bg-[#F5F7FA] hover:shadow-[0_0_20px_rgba(201,162,39,0.4)] transition-[background-color,box-shadow] duration-300"
          >
            {t.nav.request}
          </button>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-[#F5F7FA] p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.nav
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden bg-[#07111F]/95 backdrop-blur-xl border-t border-[#C9A227]/10 px-6 py-4 flex flex-col gap-1"
        >
          {LINKS.map((l) => (
            <button
              key={l.key}
              data-testid={`nav-mobile-link-${l.key}`}
              onClick={() => go(l.id)}
              className="text-left py-3 font-body text-base text-[#EDF1F6] hover:text-[#C9A227] border-b border-white/5 transition-colors duration-300"
            >
              {t.nav[l.key]}
            </button>
          ))}
          <button
            data-testid="nav-mobile-ports-button"
            onClick={() => {
              setOpen(false);
              window.dispatchEvent(new CustomEvent("mx:ports-click"));
            }}
            className="mt-3 rounded-full border border-[#C9A227]/60 px-5 py-3 text-sm font-bold tracking-widest text-[#C9A227]"
          >
            {t.ports.navLabel}
          </button>
          <button
            data-testid="nav-mobile-request-cta"
            onClick={() => go("#request")}
            className="mt-3 rounded-full bg-[#C9A227] px-5 py-3 text-sm font-bold tracking-widest text-[#07111F]"
          >
            {t.nav.request}
          </button>
        </motion.nav>
      )}
    </header>
  );
}
